import React from "react";
import { Route, Navigate } from "react-router-dom";
import Login from "@/admin/Login";
import ProtectedRoute from "@/admin/ProtectedRoute";
import AdminLayout from "@/admin/AdminLayout";
import Overview from "@/admin/Overview";
import BlogAdmin from "@/admin/BlogAdmin";
import TestimonialsAdmin from "@/admin/TestimonialsAdmin";
import MessagesAdmin from "@/admin/MessagesAdmin";
import AvailabilityAdmin from "@/admin/AvailabilityAdmin";
import BookingsAdmin from "@/admin/BookingsAdmin";
import BookingCalendar from "@/admin/BookingCalendar";
import ServicesAdmin from "@/admin/booking/ServicesAdmin";
import ContentAdmin from "@/admin/ContentAdmin";
import MediaAdmin from "@/admin/MediaAdmin";
import ProductsAdmin from "@/admin/ProductsAdmin";
import ProgramsAdmin from "@/admin/ProgramsAdmin";
import PromosAdmin from "@/admin/PromosAdmin";
import GiftCardsAdmin from "@/admin/GiftCardsAdmin";
import InvoicesAdmin from "@/admin/InvoicesAdmin";
import SettingsAdmin from "@/admin/SettingsAdmin";
import SocialAdmin from "@/admin/SocialAdmin";
import AiAssistantAdmin from "@/admin/AiAssistantAdmin";
import BrandingAdmin from "@/admin/BrandingAdmin";
import OnboardingWizard from "@/admin/OnboardingWizard";
import BillingAdmin from "@/admin/BillingAdmin";
import ComingSoonAdmin from "@/admin/ComingSoonAdmin";
import SiteBuilderWizard from "@/admin/SiteBuilderWizard";
import LearnAdmin from "@/admin/LearnAdmin";
import PathwaysAdmin from "@/admin/PathwaysAdmin";
import PathwayBuilder from "@/admin/pathways/PathwayBuilder";
import PathwayDashboard from "@/admin/pathways/PathwayDashboard";
import PathwayPreview from "@/admin/pathways/PathwayPreview";
import ModalityPacksAdmin from "@/admin/ModalityPacksAdmin";
import CustomDomainAdmin from "@/admin/CustomDomainAdmin";
import AffiliateAdmin from "@/admin/affiliate/AffiliateAdmin";
import ReviewsAdmin from "@/admin/reviews/ReviewsAdmin";
import PublicProfileAdmin from "@/admin/PublicProfileAdmin";
import TelehealthAdmin from "@/admin/TelehealthAdmin";
import ConsultRoomPage from "@/admin/telehealth/ConsultRoomPage";
import QuizReviewsAdmin from "@/admin/QuizReviewsAdmin";
import PaymentsAdmin from "@/admin/PaymentsAdmin";
import LearnAnalyticsAdmin from "@/admin/LearnAnalyticsAdmin";
import TaxAdmin from "@/admin/TaxAdmin";
import SiteImagesAdmin from "@/admin/SiteImagesAdmin";
import RequireCapability from "@/admin/entitlements/RequireCapability";
import { CAP } from "@/admin/entitlements/capabilities";
// crm-module BEGIN C1.01
import CrmShell from "@/admin/crm/CrmShell";
import { crmRouteChildren } from "@/admin/crm/crmRoutes";
// crm-module END
// crm-module BEGIN C3.02 (lane F5)
import JoinPage from "@/admin/crm/JoinPage";
// crm-module END
// crm-module BEGIN C3.02 — helper shell scoping (decision 5)
import RequireOwnerAdmin from "@/admin/RequireOwnerAdmin";
// crm-module END

export const adminRoutes = (
  <>
    <Route path="/admin/login" element={<Login />} />
    {/* crm-module BEGIN C3.02 (lane F5) — OUTSIDE ProtectedRoute: an invited helper who
        has not yet accepted owns no tenant, so the ProtectedRoute bootstrap must not run
        for them (see JoinPage's own docstring / CR-F5-1). */}
    <Route path="/admin/join" element={<JoinPage />} />
    {/* crm-module END */}
    <Route
      path="/admin"
      element={
        <ProtectedRoute>
          <AdminLayout />
        </ProtectedRoute>
      }
    >
      {/* crm-module BEGIN C1.01 — the CRM section: layout route + the shared child fragment (admin/crm/crmRoutes.jsx) */}
      <Route path="crm" element={<RequireCapability capability={CAP.crm}><CrmShell /></RequireCapability>}>{crmRouteChildren}</Route>
      {/* crm-module END */}
      {/* crm-module BEGIN C3.02 — helper shell scoping (decision 5, role matrix). ONE
          pathless guard over every sibling of `crm` above, on BOTH hosts
          (tenant/TenantSite.jsx carries the text-identical line) — not a check added to
          each page below. A CRM helper is redirected to `crm` here before any of these
          thirty-odd pages mounts and discovers, each on its own, the 403 the server was
          always going to answer it with. */}
      <Route element={<RequireOwnerAdmin />}>
      {/* crm-module END */}
      <Route index element={<Overview />} />
      <Route path="blog" element={<BlogAdmin />} />
      <Route path="testimonials" element={<TestimonialsAdmin />} />
      <Route path="messages" element={<MessagesAdmin />} />
      <Route path="availability" element={<AvailabilityAdmin />} />
      <Route path="bookings" element={<BookingsAdmin />} />
      <Route path="calendar" element={<BookingCalendar />} />
      <Route path="booking-services" element={<ServicesAdmin />} />
      {/* Alias: /admin/services is the intuitive URL for the booking-services catalogue;
          redirect it to the canonical path instead of falling through to a blank page.
          RELATIVE, and text-identical in tenant/TenantSite.jsx: the sibling route of the
          tree it was typed in, so /t/{slug}/admin/services stays in that practice. */}
      <Route path="services" element={<Navigate to="../booking-services" replace />} />
      <Route path="products" element={<ProductsAdmin />} />
      <Route path="programs" element={<ProgramsAdmin />} />
      <Route path="promos" element={<PromosAdmin />} />
      <Route path="giftcards" element={<GiftCardsAdmin />} />
      <Route path="invoices" element={<InvoicesAdmin />} />
      <Route path="content" element={<ContentAdmin />} />
      <Route path="media" element={<MediaAdmin />} />
      <Route path="site-images" element={<SiteImagesAdmin />} />
      <Route path="settings" element={<SettingsAdmin />} />
      {/* Plan-gated pages (here and below): RequireCapability renders the page only when
          the practice's plan includes the capability and it is built, with the same key
          its sidebar entry carries (admin/layout/adminNav.js). */}
      <Route path="ai" element={<RequireCapability capability={CAP.aiAssistant}><AiAssistantAdmin /></RequireCapability>} />
      {/* crm-module BEGIN C2.05 — /admin/automation is retired: Email Automation lives at
          /admin/crm/email (decision 8, Sequences replace the old rule engine); no redirect,
          the old page is gone. */}
      {/* crm-module END */}
      <Route path="social" element={<RequireCapability capability={CAP.social}><SocialAdmin /></RequireCapability>} />
      {/* Self-service */}
      <Route path="branding" element={<BrandingAdmin />} />
      <Route path="profile" element={<PublicProfileAdmin />} />
      <Route path="onboarding" element={<OnboardingWizard />} />
      <Route path="site-builder" element={<SiteBuilderWizard />} />
      {/* Academy pages are NOT plan-gated: the server gates course authoring only,
          and a downgraded practice still grades and follows its students.
          LearnAdmin withholds its own authoring controls. */}
      <Route path="learn" element={<LearnAdmin />} />
      <Route path="pathways" element={<PathwaysAdmin />} />
      {/* Pathways v3 (contract §9): the builder (new, or ?step=basics|structure of an
          existing one), the learner preview, and the tracking dashboard. */}
      <Route path="pathways/new" element={<PathwayBuilder />} />
      <Route path="pathways/:pathwayId/edit" element={<PathwayBuilder />} />
      <Route path="pathways/:pathwayId/preview" element={<PathwayPreview />} />
      <Route path="pathways/:pathwayId" element={<PathwayDashboard />} />
      <Route path="learn-analytics" element={<LearnAnalyticsAdmin />} />
      <Route path="quiz-reviews" element={<QuizReviewsAdmin />} />
      <Route path="payments" element={<PaymentsAdmin />} />
      <Route path="tax-config" element={<TaxAdmin />} />
      {/* crm-module BEGIN C1.01 — the old Clients page is the CRM Clients list now */}
      <Route path="customers" element={<Navigate to="../crm/clients" replace />} />
      {/* crm-module END */}
      {/* NOT plan-gated, deliberately: paid consultations must stay joinable and
            completable after a downgrade. The page withholds only NEW video business
            (TelehealthAdmin, ServicesAdmin); care.video_rooms is enforced server-side. */}
        <Route path="telehealth" element={<TelehealthAdmin />} />
      {/* The practitioner's per-minute consult room (BUG #3 fix) — no sidebar entry,
          reached only from a room row's join action. */}
      <Route path="telehealth/consult/:appointmentId" element={<ConsultRoomPage />} />
      <Route path="modality-packs" element={<ModalityPacksAdmin />} />
      {/* NOT plan-gated: a downgraded practice must still see its domain. Setting and
            verifying one is withheld inside CustomDomainAdmin. */}
        <Route path="custom-domain" element={<CustomDomainAdmin />} />
      <Route path="affiliate" element={<AffiliateAdmin />} />
      <Route path="reviews" element={<ReviewsAdmin />} />
      <Route path="billing" element={<BillingAdmin />} />
      <Route path="coming-soon" element={<ComingSoonAdmin />} />
      </Route>
    </Route>
  </>
);
